import { X } from "lucide-react";
import { useId, useRef, useState } from "react";

import { FieldShell } from "@/components/ui/Field";
import { tone, type Tone } from "@/components/ui/tone";
import { cn } from "@/lib/cn";
import { holdsSeparator, isSeparatorKey, scanEmails } from "@/lib/emails";
import { useT } from "@/lib/i18n";

export type EmailInputProps = {
  label: string;
  value: string[];
  onChange: (emails: string[]) => void;
  hint?: string | undefined;
  placeholder?: string | undefined;
  hideLabel?: boolean | undefined;
  tone?: Tone | undefined;
  disabled?: boolean | undefined;

  exclude?: readonly string[] | undefined;
};

export function EmailInput({
  label,
  value,
  onChange,
  hint,
  placeholder,
  hideLabel,
  tone: which,
  disabled = false,
  exclude,
}: EmailInputProps) {
  const id = useId();
  const t = useT();
  const palette = tone(which);
  const input = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState("");
  const [rejected, setRejected] = useState<string[]>([]);

  const commit = (text: string): void => {
    if (text.trim() === "") {
      setDraft("");
      setRejected([]);
      return;
    }
    const scan = scanEmails(text, exclude === undefined ? value : [...value, ...exclude]);
    if (scan.accepted.length > 0) onChange([...value, ...scan.accepted]);

    setDraft(scan.rejected.join(", "));
    setRejected(scan.rejected);
  };

  const remove = (address: string): void => {
    onChange(value.filter((entry) => entry !== address));
    input.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      commit(draft);
      return;
    }
    if (isSeparatorKey(event.key)) {
      event.preventDefault();
      commit(draft);
      return;
    }

    if (event.key === "Backspace" && draft === "" && value.length > 0) {
      event.preventDefault();
      const last = value.at(-1);
      if (last !== undefined) {
        onChange(value.slice(0, -1));
        setDraft(last);
      }
    }
  };

  const invalid = rejected.length > 0;

  return (
    <FieldShell
      describedBy={`${id}-help`}
      error={invalid ? t("emails.notAddresses", { list: rejected.join("، ") }) : undefined}
      hideLabel={hideLabel}
      hint={hint}
      htmlFor={id}
      label={label}
      tone={which}
    >
      {}
      <div
        className={cn(
          "flex flex-wrap items-center gap-1.5 rounded-xl border px-2 py-2 transition-colors",
          "min-h-11 cursor-text focus-within:border-brand",
          palette.control,
          invalid && "border-danger",
          disabled && "cursor-not-allowed opacity-60",
        )}
        onPointerDown={(event) => {
          if (event.target === event.currentTarget) input.current?.focus();
        }}
      >
        {value.map((address) => (
          <span
            className={cn(
              "inline-flex max-w-full items-center gap-1 rounded-lg py-1 ps-2.5 pe-1 text-sm",
              palette.active,
              palette.text,
            )}
            key={address}
          >
            <span className="min-w-0 truncate" dir="ltr">
              {address}
            </span>
            <button
              aria-label={t("emails.remove", { email: address })}
              className={cn(
                "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
                palette.muted,
                palette.hover,
              )}
              disabled={disabled}
              onClick={() => {
                remove(address);
              }}
              type="button"
            >
              <X aria-hidden className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}

        <input
          aria-describedby={`${id}-help`}
          aria-invalid={invalid}
          autoCapitalize="none"
          autoComplete="email"
          autoCorrect="off"
          className={cn(
            "min-w-40 flex-1 bg-transparent px-1.5 py-1 text-sm outline-none",
            "placeholder:opacity-60 disabled:cursor-not-allowed",
            palette.text,
          )}
          dir="ltr"
          disabled={disabled}
          id={id}
          inputMode="email"
          onBlur={() => {
            commit(draft);
          }}
          onChange={(event) => {
            const next = event.target.value;

            const filled = (event.nativeEvent as InputEvent).inputType === "insertReplacementText";
            if (filled || holdsSeparator(next)) {
              commit(next);
              return;
            }
            setDraft(next);
            if (rejected.length > 0) setRejected([]);
          }}
          onKeyDown={onKeyDown}
          onPaste={(event) => {
            const text = event.clipboardData.getData("text");
            if (text === "") return;
            event.preventDefault();
            commit(draft === "" ? text : `${draft} ${text}`);
          }}
          placeholder={value.length === 0 ? placeholder : ""}
          ref={input}
          spellCheck={false}
          type="text"
          value={draft}
        />
      </div>
    </FieldShell>
  );
}
